// Pure rules ported from coffee/users/user_detail.coffee and templates/users/edit-user-tpl.hbs.
// No globals: everything comes in as arguments so the rules are testable on their own.

export const ROLE_INTERNAL_ADMIN = 'ROLE_SUPER_ADMIN';

// Same order as Models.User::roles (lowest → highest privilege).
export const ROLE_OPTIONS = [
  { value: 'ROLE_RESTRICTED_USER', label: 'Restricted User' },
  { value: 'ROLE_DASHBOARD_USER', label: 'Dashboard User' },
  { value: 'ROLE_USER_MINUS', label: 'Read Only User' },
  { value: 'ROLE_USER', label: 'User' },
  { value: 'ROLE_NTK', label: 'NTK User' },
  { value: 'ROLE_USER_PLUS', label: 'Dashboard Admin' },
  { value: 'ROLE_CAMPAIGN_ADMIN', label: 'Workspace Admin' },
  { value: 'ROLE_ADMIN', label: 'Account Admin' },
  { value: ROLE_INTERNAL_ADMIN, label: 'Internal Admin' },
];

const ROLE_ORDER = ROLE_OPTIONS.map((o) => o.value);

// What getFormFields sent to Bacon for each select value.
export const ROLES_BY_VALUE = {
  [ROLE_INTERNAL_ADMIN]: ['ROLE_USER', 'ROLE_USER_PLUS', 'ROLE_CAMPAIGN_ADMIN', 'ROLE_ADMIN', ROLE_INTERNAL_ADMIN],
  ROLE_ADMIN: ['ROLE_USER', 'ROLE_USER_PLUS', 'ROLE_CAMPAIGN_ADMIN', 'ROLE_ADMIN'],
  ROLE_CAMPAIGN_ADMIN: ['ROLE_USER', 'ROLE_USER_PLUS', 'ROLE_CAMPAIGN_ADMIN'],
  ROLE_USER_PLUS: ['ROLE_USER', 'ROLE_USER_PLUS'],
  ROLE_USER_MINUS: ['ROLE_USER_MINUS'],
  ROLE_NTK: ['ROLE_NTK'],
  ROLE_DASHBOARD_USER: ['ROLE_DASHBOARD_USER'],
  ROLE_RESTRICTED_USER: ['ROLE_RESTRICTED_USER'],
  ROLE_USER: ['ROLE_USER'],
};

export const USER_TYPE_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'uncontracted', label: 'Uncontracted' },
  { value: 'internal', label: 'Internal' },
];

// Util.ColorThemes.getOptionsMeta minus "default" and "dark" (ENG-5935) leaves only light.
export const THEME_OPTIONS = [{ value: 'light', label: 'Light Mode' }];

const has = (roles, role) => Array.isArray(roles) && roles.indexOf(role) > -1;

// Mirrors the Models.User predicates used by renderRolesSelect.
export const roleValueFromRoles = (roles = []) => {
  if (has(roles, ROLE_INTERNAL_ADMIN)) { return ROLE_INTERNAL_ADMIN; }
  if (has(roles, 'ROLE_ADMIN')) { return 'ROLE_ADMIN'; }
  if (has(roles, 'ROLE_CAMPAIGN_ADMIN')) { return 'ROLE_CAMPAIGN_ADMIN'; }
  if (has(roles, 'ROLE_USER_PLUS')) { return 'ROLE_USER_PLUS'; }
  if (has(roles, 'ROLE_USER_MINUS') && !has(roles, 'ROLE_USER')) { return 'ROLE_USER_MINUS'; }
  if (has(roles, 'ROLE_NTK')) { return 'ROLE_NTK'; }
  if (has(roles, 'ROLE_DASHBOARD_USER') && !has(roles, 'ROLE_USER')) { return 'ROLE_DASHBOARD_USER'; }
  if (has(roles, 'ROLE_RESTRICTED_USER')) { return 'ROLE_RESTRICTED_USER'; }
  return 'ROLE_USER';
};

export const highestRole = (roles) => {
  let position = 0;
  (roles || []).forEach((r) => {
    const index = ROLE_ORDER.indexOf(r);
    if (index > position) { position = index; }
  });
  return ROLE_ORDER[position];
};

export const hasMinimumRole = (roles, role) =>
  ROLE_ORDER.indexOf(highestRole(roles)) >= ROLE_ORDER.indexOf(role);

const currentRoles = (currentUser) => (currentUser && currentUser.user && currentUser.user.roles) || [];
const isFullAccessInternalAdmin = (currentUser) =>
  !!currentUser && currentUser.isInternalAdmin === true && !!currentUser.user && currentUser.user.fullAccountAccessEnabled === true;

export const roleOptionsFor = ({ currentUser, targetRoles = [], isNew }) => {
  const editorIsInternalAdmin = !!currentUser && currentUser.isInternalAdmin === true;
  if (has(targetRoles, ROLE_INTERNAL_ADMIN)) {
    return editorIsInternalAdmin
      ? { options: ROLE_OPTIONS, disabled: false }
      : { options: ROLE_OPTIONS.filter((o) => o.value === ROLE_INTERNAL_ADMIN), disabled: true };
  }
  let options = ROLE_OPTIONS;
  if (!editorIsInternalAdmin) {
    options = options.filter((o) => o.value !== ROLE_INTERNAL_ADMIN);
    if (isNew) { options = options.filter((o) => o.value !== 'ROLE_NTK'); }
  }
  if (!(currentUser && currentUser.isAdmin === true)) {
    options = options.filter((o) => o.value !== 'ROLE_ADMIN');
  }
  return { options, disabled: false };
};

export const canEditRoles = (currentUser) => hasMinimumRole(currentRoles(currentUser), 'ROLE_CAMPAIGN_ADMIN');
export const canEditCampaigns = (currentUser) => !!currentUser && (currentUser.isAdmin === true || currentUser.isInternalAdmin === true);

export const showAdditionalAccounts = ({ currentUser, targetUserId, roleValue, isNew }) => {
  if (!currentUser || currentUser.isInternalAdmin !== true) { return false; }
  if (roleValue !== ROLE_INTERNAL_ADMIN) { return false; }
  if (isFullAccessInternalAdmin(currentUser)) { return true; }
  return !isNew && !!targetUserId && targetUserId === currentUser.userId;
};
export const canEditAdditionalAccounts = (currentUser) => isFullAccessInternalAdmin(currentUser);

export const showFullAccountAccess = ({ currentUser, roleValue }) =>
  !!currentUser && currentUser.isInternalAdmin === true && roleValue === ROLE_INTERNAL_ADMIN;

export const discoverToggle = ({ roleValue, savedDiscoverEnabled }) => (
  roleValue === 'ROLE_RESTRICTED_USER'
    ? { enabled: true, checked: savedDiscoverEnabled === true }
    : { enabled: false, checked: true }
);

export const visibleCampaigns = (campaigns = [], account, additionalAccounts = []) => {
  const accounts = [account].concat(additionalAccounts || []);
  return campaigns.filter((c) => accounts.indexOf(c.account) > -1);
};

// Helper.formatTimezoneItem
export const formatTimezoneLabel = (tz) => {
  if (tz.indexOf('/') === -1) { return tz; }
  const parts = tz.split('/');
  parts.shift();
  return parts.join('/').split('_').join(' ');
};

// renderTimezone: anything but a real zone falls back to the browser's guess (was jstz).
export const defaultTimezone = (tz) => (
  tz && tz !== 'EST' ? tz : Intl.DateTimeFormat().resolvedOptions().timeZone
);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_PATTERN = /^[\w.+\-@]+$/;

export const initialValues = (user, { isNew, account, campaigns = [] } = {}) => {
  const u = user || {};
  return {
    username: u.username || '',
    email: u.email || '',
    phone: u.phone || '',
    first_name: u.first_name || '',
    last_name: u.last_name || '',
    title: u.title || '',
    timezone: defaultTimezone(u.timezone),
    password: '',
    confirm_password: '',
    additionalAccounts: u.additionalAccounts || [],
    campaigns: isNew ? campaigns : (u.campaigns || []),
    role: roleValueFromRoles(u.roles),
    type: u.type || 'standard',
    theme: 'light', // forced: dark mode is hidden (ENG-5935)
    enabled: isNew ? true : u.enabled === true,
    autoTranslationLang: u.autoTranslationLang === undefined ? null : u.autoTranslationLang,
    detectEnabled: u.detectEnabled === true,
    discoverEnabled: u.discoverEnabled === true,
    narrativesEnabled: u.narrativesEnabled === true,
    isAutomated: u.isAutomated === true,
    apiEnabled: u.apiEnabled === true,
    fullAccountAccessEnabled: u.fullAccountAccessEnabled === true,
    account: isNew ? account : u.account,
  };
};

export const validate = (values, { isNew, canEditRoles: rolesEditable, canEditCampaigns: campaignsEditable, canEditType, phoneValid }) => {
  const errors = {};
  const username = (values.username || '').trim();
  const firstName = (values.first_name || '').trim();
  const lastName = (values.last_name || '').trim();
  const title = (values.title || '').trim();
  if (isNew) {
    if (!username) { errors.username = 'Username is required'; }
    else if (!USERNAME_PATTERN.test(username)) { errors.username = 'No special characters allowed'; }
    else if (username.length > 64) { errors.username = 'Username must be at most 64 characters'; }
  }
  if (!firstName) { errors.first_name = 'First name is required'; }
  else if (firstName.length > 64) { errors.first_name = 'First name must be at most 64 characters'; }
  if (!lastName) { errors.last_name = 'Last name is required'; }
  else if (lastName.length > 64) { errors.last_name = 'Last name must be at most 64 characters'; }
  if (title.length > 64) { errors.title = 'Title must be at most 64 characters'; }
  const email = (values.email || '').trim();
  if (!email) { errors.email = 'Email is required'; }
  else if (email.length < 6 || email.length > 254) { errors.email = 'Email must be between 6 and 254 characters'; }
  else if (!EMAIL_PATTERN.test(email)) { errors.email = 'Email must be a valid email'; }

  const password = (values.password || '').trim();
  const confirm = (values.confirm_password || '').trim();
  const lengthOk = password.length >= 6 && password.length <= 64;
  if ((isNew && !lengthOk) || (!isNew && password !== '' && !lengthOk)) { errors.password = 'Password is required.'; }
  else if (confirm !== password) { errors.confirm_password = 'Passwords must match.'; }

  if (rolesEditable && canEditType && values.role === ROLE_INTERNAL_ADMIN && values.type !== 'internal') {
    errors.type = 'User Type must be Internal for Internal Admin role.';
  }
  if ((isNew || campaignsEditable) && (!values.campaigns || values.campaigns.length === 0)) {
    errors.campaigns = 'A campaign is required. Use the active switch to prevent a user from logging in.';
  }
  if (values.phone && phoneValid === false) { errors.phone = 'Enter a valid phone number'; }
  return errors;
};

// getFormFields + _updateAccountAccess, applied over the whole loaded user the way
// Backbone's model.save sent every attribute. Bacon's updateUser does userModel.set(body).
// context.campaigns (the workspace list the form offers) drops picks outside the selected accounts.
export const buildPayload = (values, { screen, isNew, currentUser, loadedUser, campaigns }) => {
  const base = { ...(loadedUser || {}) };
  delete base.phone_number_verified;
  // Only a password typed into this form is sent; never one carried on the loaded user.
  delete base.password;
  delete base.confirm_password;
  const payload = {
    ...base,
    username: (values.username || '').trim(),
    email: (values.email || '').trim(),
    first_name: (values.first_name || '').trim(),
    last_name: (values.last_name || '').trim(),
    title: values.title,
    timezone: values.timezone,
    enabled: values.enabled === true,
    theme: 'light', // forced: dark mode is hidden (ENG-5935)
    isAutomated: values.isAutomated === true,
    apiEnabled: values.apiEnabled === true,
    autoTranslationLang: values.autoTranslationLang === '' ? null : values.autoTranslationLang,
    detectEnabled: values.detectEnabled === true,
    narrativesEnabled: values.narrativesEnabled === true,
  };
  if (isNew) { payload.account = values.account; }

  // Send the phone only when it changed (a deliberate clear sends ''), so an untouched
  // number is never rewritten. Create always carries it.
  const phone = (values.phone || '').trim();
  const savedPhone = ((loadedUser && loadedUser.phone) || '').trim();
  if (isNew || phone !== savedPhone) { payload.phone = phone; } else { delete payload.phone; }

  if (isNew || values.password || values.confirm_password) {
    payload.password = values.password;
    payload.confirm_password = values.confirm_password;
  }
  // Backbone sent the whole new model, so create always carries the campaigns.
  if (isNew || canEditCampaigns(currentUser)) {
    if (Array.isArray(campaigns) && campaigns.length > 0 && Array.isArray(values.campaigns)) {
      const visibleIds = visibleCampaigns(campaigns, values.account, values.additionalAccounts).map((c) => c.id);
      payload.campaigns = values.campaigns.filter((id) => visibleIds.indexOf(id) > -1);
    } else {
      payload.campaigns = values.campaigns;
    }
  }
  if (currentUser && currentUser.isInternalAdmin === true) {
    if (values.type) { payload.type = values.type; }
  } else if (isNew) {
    payload.type = 'standard';
  }

  const applyAccountAccess = () => {
    if (canEditAdditionalAccounts(currentUser)) {
      if (values.additionalAccounts) { payload.additionalAccounts = values.additionalAccounts; }
      payload.fullAccountAccessEnabled = values.fullAccountAccessEnabled === true;
    }
  };

  if (screen !== 'mySettings') {
    if (values.role === ROLE_INTERNAL_ADMIN) {
      if (currentUser && currentUser.isInternalAdmin === true) { payload.roles = ROLES_BY_VALUE[ROLE_INTERNAL_ADMIN]; }
      applyAccountAccess();
    } else {
      payload.roles = ROLES_BY_VALUE[values.role] || ROLES_BY_VALUE.ROLE_USER;
    }
  } else {
    applyAccountAccess();
  }

  // My Settings leaves discoverEnabled as loaded; admin screens only let restricted users set it.
  if (screen !== 'mySettings') {
    const roles = payload.roles || [];
    payload.discoverEnabled = roles[0] === 'ROLE_RESTRICTED_USER' ? values.discoverEnabled === true : false;
  }
  return payload;
};
